import type { ResultOf } from 'gql.tada';
import { graphql } from '@/lib/graphql';

export const SystemOrganizationsSubscription = graphql(`
  subscription SystemOrganizations {
    organizations(orderBy: { name: ASC }) {
      id
      name
      slug
      createdAt
      crmIntegration {
        id
        name
      }
      membersAggregate {
        aggregate {
          count
        }
      }
      phoneNumbersAggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

export const SystemOrganizationSubscription = graphql(`
  subscription SystemOrganization($id: Uuid!) {
    organizationsByPk(id: $id) {
      id
      name
      slug
      apiKey
      createdAt
      crmIntegrationId
      crmIntegration {
        id
        name
        type
        config
      }
      membersAggregate {
        aggregate {
          count
        }
      }
      phoneNumbersAggregate {
        aggregate {
          count
        }
      }
    }
  }
`);

export const SystemOrganizationsWithCrmSubscription = graphql(`
  subscription SystemOrganizationsWithCrm {
    organizations(where: { crmIntegrationId: { _isNull: false } }, orderBy: { name: ASC }) {
      id
      name
    }
  }
`);

export const SystemOrganizationMembersSubscription = graphql(`
  subscription SystemOrganizationMembers($organizationId: Uuid!) {
    organizationMembers(where: { organizationId: { _eq: $organizationId } }, orderBy: { email: ASC }) {
      id
      email
      role
      status
      joinedAt
      userId
    }
  }
`);

export const SystemOrganizationPhoneNumbersSubscription = graphql(`
  subscription SystemOrganizationPhoneNumbers($organizationId: Uuid!) {
    phoneNumbers(where: { organizationId: { _eq: $organizationId } }, orderBy: { phoneNumber: ASC }) {
      id
      phoneNumber
      friendlyName
      status
      crmIntegrationId
      crmIntegration {
        id
        name
        type
        config
      }
    }
  }
`);

export const SystemOrganizationWidgetsSubscription = graphql(`
  subscription SystemOrganizationWidgets($organizationId: Uuid!) {
    organizationWidgets(where: { organizationId: { _eq: $organizationId } }) {
      id
      widgetId
      enabled
    }
  }
`);

export const UpdateSystemOrganizationMutation = graphql(`
  mutation UpdateSystemOrganization($id: Uuid!, $name: String!, $slug: String!, $updatedAt: Timestamptz!) {
    updateOrganizationsByPk(pkColumns: { id: $id }, _set: { name: $name, slug: $slug, updatedAt: $updatedAt }) {
      id
      name
      slug
    }
  }
`);

export const UpdateCrmIntegrationMutation = graphql(`
  mutation UpdateCrmIntegration($id: Uuid!, $name: String!, $config: Jsonb!, $updatedAt: Timestamptz!) {
    updateCrmIntegrationsByPk(pkColumns: { id: $id }, _set: { name: $name, config: $config, updatedAt: $updatedAt }) {
      id
      name
      config
    }
  }
`);

export const CreateCrmIntegrationMutation = graphql(`
  mutation CreateCrmIntegration($name: String!, $type: String!, $config: Jsonb!) {
    insertCrmIntegrationsOne(object: { name: $name, type: $type, config: $config }) {
      id
    }
  }
`);

export const DeleteOrphanCrmIntegrationMutation = graphql(`
  mutation DeleteOrphanCrmIntegration($id: Uuid!) {
    deleteCrmIntegrations(where: { id: { _eq: $id }, _not: { _or: [{ organizations: {} }, { phoneNumbers: {} }] } }) {
      affectedRows
    }
  }
`);

export const AssignPhoneNumberCrmIntegrationMutation = graphql(`
  mutation AssignPhoneNumberCrmIntegration($phoneNumberId: Uuid!, $crmIntegrationId: Uuid!, $updatedAt: Timestamptz!) {
    updatePhoneNumbersByPk(
      pkColumns: { id: $phoneNumberId }
      _set: { crmIntegrationId: $crmIntegrationId, updatedAt: $updatedAt }
    ) {
      id
      crmIntegrationId
    }
  }
`);

export const RemovePhoneNumberCrmOverrideMutation = graphql(`
  mutation RemovePhoneNumberCrmOverride($phoneNumberId: Uuid!, $crmIntegrationId: Uuid!, $updatedAt: Timestamptz!) {
    updatePhoneNumbersByPk(pkColumns: { id: $phoneNumberId }, _set: { crmIntegrationId: null, updatedAt: $updatedAt }) {
      id
      crmIntegrationId
    }
    deleteCrmIntegrations(
      where: { id: { _eq: $crmIntegrationId }, _not: { _or: [{ organizations: {} }, { phoneNumbers: {} }] } }
    ) {
      affectedRows
    }
  }
`);

export type SystemOrganization = NonNullable<ResultOf<typeof SystemOrganizationSubscription>['organizationsByPk']>;

export type SystemOrganizationMember = ResultOf<
  typeof SystemOrganizationMembersSubscription
>['organizationMembers'][number];

export type SystemOrganizationPhoneNumber = ResultOf<
  typeof SystemOrganizationPhoneNumbersSubscription
>['phoneNumbers'][number];
